import _ from 'lodash';
import cn from 'classnames';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { addSuccessNotification } from 'shared/lib/notifications';
import { scrollToId, scrollToTop } from 'shared/lib/scrollTo';
import { propTypes as jobPropTypes } from 'shared/proptypes/Job';
import Alert from 'shared/components/Alert';
import TranslateError from 'shared/components/TranslateError';
import CloseButton from 'shared/components/buttons/Close';
import googleAnalytics from 'userportal/lib/googleAnalytics';
import { actions as applicationActions } from 'userportal/ducks/applications';
import { selectors as profileSelectors } from 'userportal/ducks/profile';
import { selectors as authSelectors } from 'userportal/ducks/auth';
import JobApplicationMessageForm from './MessageForm';
import styles from './Form.module.scss';

export const JobApplicationForm = ({
  job,
  title,
  onClose,
  offerId,
  className,
  gaEvent,
  loggedIn,
}) => {
  const [error, setError] = useState(null);
  const { t } = useTranslation('userportal');
  const dispatch = useDispatch();

  const user = useSelector(authSelectors.getUser);
  const attachments = useSelector(profileSelectors.getAttachments);
  const userProfile = useSelector(profileSelectors.getUserProfile);
  const processing = useSelector(state => state.applications._processing);

  useEffect(() => {
    if (gaEvent) {
      googleAnalytics.event(gaEvent, { loggedIn });
    }
    scrollToId('job-application-form');
    return () => {
      scrollToTop();
    };
  }, []);

  const onSubmit = (data) => {
    const hasExpectedSalary = !!(~~data.expectedSalary > 0);
    dispatch(applicationActions.addApplication(
      loggedIn,
      job.id, {
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        phone: data.phone,
        linkedIn: data.linkedIn,
        message: data.message,
        createProfile: data.createProfile,
        termsAgreement: data.termsAgreement,
        attachments: _.keys(data.attachments),
        files: data.files || [],
        note: data.note,
        offerId,
        expectedSalary: hasExpectedSalary ? ~~data.expectedSalary : undefined,
        expectedSalaryPeriod: hasExpectedSalary ? data.expectedSalaryPeriod : undefined,
        expectedSalaryCurrency: hasExpectedSalary ? data.expectedSalaryCurrency : undefined,
        availability: (data.availability || '').trim() || undefined,
      },
      () => {
        onClose();
        // TODO: data.createProfile? -> info about mail
        addSuccessNotification(t('notification.applicationSent'));
        googleAnalytics.event('job_application');
        if (offerId) {
          googleAnalytics.event('job_offer_accept');
        }
        scrollToTop();
      }, (err) => {
        setError(err.error);
      }
    ));
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className={cn(styles.form, className)}>
          <div id="job-application-form" className={styles.title}>
            <h3>{title || t('page.job.applyButton')}</h3>
            <CloseButton onClick={onClose}>{t('common:linkButton.close')}</CloseButton>
          </div>
          {error && (
            <Alert variant="error">
              <TranslateError>{error}</TranslateError>
            </Alert>
          )}
          <JobApplicationMessageForm
            className={styles.messageForm}
            type="apply"
            jobTitle={job.title}
            companyId={job.company.id}
            companyName={job.company.name}
            resumeRequired={!loggedIn || job.resumeRequired} // TODO: always require CV for anonymous
            processing={processing}
            onSubmit={onSubmit}
            onCancel={onClose}
            user={user}
            attachments={attachments}
            isFromOffer={!!offerId}
            scrollToError={false}
            requirePhone={loggedIn ? !userProfile.phone : true}
          />
        </div>
      </div>
    </div>
  );
};

JobApplicationForm.propTypes = {
  job: PropTypes.shape(jobPropTypes).isRequired,
  title: PropTypes.string,
  onClose: PropTypes.func,
  offerId: PropTypes.string,
  className: PropTypes.string,
  gaEvent: PropTypes.string,
  loggedIn: PropTypes.bool,
};

JobApplicationForm.defaultProps = {
  title: null,
  onClose() {},
  offerId: null,
  className: null,
  gaEvent: null,
  loggedIn: false,
};

export default JobApplicationForm;
